import { zodResolver } from '@hookform/resolvers/zod';
import {
  MakePaymentForm,
  MakePaymentFormInput,
  MakePaymentFormSchema,
} from '@monetize/types/app';
import { toDateOnly } from '@monetize/utils/core';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateAccountManualPayment } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import { MButton, MCenterModal, MStack } from '../../../components/Monetize';
import { useCurrencies } from '../../../hooks/useCurrencies';
import { CreatePaymentWithManualPayment } from '../../Invoices/createPayment/CreatePaymentWithManualPayment';

interface AccountManualPaymentModalProps {
  accountId: string;
  /** Seeds the payment's currency; the user can change it when the tenant has more than one. */
  accountCurrency: string;
  isOpen: boolean;
  onClose: (reload: boolean) => void;
}

export const AccountManualPaymentModal = ({
  accountId,
  accountCurrency,
  isOpen,
  onClose,
}: AccountManualPaymentModalProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { currencies } = useCurrencies();
  const { mutateAsync: doCreateAccountManualPayment } =
    useCreateAccountManualPayment();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<MakePaymentFormInput, unknown, MakePaymentForm>({
    resolver: zodResolver(MakePaymentFormSchema),
    mode: 'onChange',
    defaultValues: {
      type: 'manual',
      referenceNo: '',
      currency: accountCurrency,
      amount: undefined,
      paymentDate: toDateOnly(new Date(), 'userTimezone'),
      description: '',
    },
  });

  const currency = watch('currency');

  const onSubmit = async (formData: MakePaymentForm) => {
    setIsSaving(true);
    try {
      await doCreateAccountManualPayment({
        accountId,
        payload: {
          amount: formData.amount,
          paymentDate: formData.paymentDate,
          referenceNo: formData.referenceNo,
          description: formData.description,
          currency: formData.currency,
        },
      });
      handleClose(true);
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setIsSaving(false);
    }
  };

  function handleClose(reload = false) {
    onClose(reload);
    reset();
  }

  return (
    <MCenterModal
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle="New Payment"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            onClick={() => handleClose()}
            variant="cancel"
            minW="auto"
            isDisabled={isSaving}
          >
            Cancel
          </MButton>
          <MButton
            form="account-manual-payment-form"
            type="submit"
            variant="primary"
            isDisabled={!isValid}
            isLoading={isSaving}
            minW="auto"
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <form id="account-manual-payment-form" onSubmit={handleSubmit(onSubmit)}>
        <CreatePaymentWithManualPayment
          control={control}
          errors={errors}
          currency={currency}
          isCurrencyEditable={currencies.length > 1}
          isSaving={isSaving}
          allowPartialPayment
        />
      </form>
    </MCenterModal>
  );
};
